import type {
  AuthenticateBillingAddress,
  AuthenticateInput,
  AuthenticateResult,
  ThreeDsBypassReason,
  ThreeDsDefaultBillingAddress,
  ThreeDsPreflightResponse,
  ThreeDsProof,
} from './types';

/**
 * Autenticação 3DS browser-first (docs/3ds-plan.md F4): pré-flight → desafio
 * (Stone ou simulado) → comprovante. NUNCA lança — falha ANTES de o desafio
 * aparecer vira `{ status: 'bypassed' }` (3DS indisponível não mata a venda);
 * desafio EXIBIDO ao comprador e não concluído (timeout, abandono, erro) vira
 * `{ status: 'failed' }` — a venda não segue (D15).
 */

// Fase sem interação (pré-auth + 3DS Method + montagem do iframe).
const CHALLENGE_TIMEOUT_MS = 60_000;
// Quando o iframe do desafio aparece, o relógio passa a ser do comprador:
// aprovar no app do banco leva minutos (o timeout do Itaú foi o que revelou
// que 60s não bastam). O ACS tem prazo próprio e resolve o init sozinho ao
// estourar; este é só o teto de segurança client-side.
const CHALLENGE_VISIBLE_TIMEOUT_MS = 300_000;
// O token 3DS da Stone vale ~20s ("20 seconds of validity" no manual). Se o
// carregamento do script consumiu boa parte disso, mintamos um token fresco
// com um segundo pré-flight antes do init — senão o init morre em INVALID_JWT.
const TOKEN_STALE_MS = 10_000;
// Duração do desafio simulado (espelha FAKE_3DS_CHALLENGE_DELAY_MS do backend).
const FAKE_CHALLENGE_MS = 8_000;

type PreflightFetcher = () => Promise<ThreeDsPreflightResponse>;

/**
 * Bypass com diagnóstico: o motivo cru fica no resultado (`bypassDetail`, que o
 * checkout repassa ao backend pra log) e no console — sem isso, todo fluxo que
 * a Stone recusa vira um "unavailable" impossível de investigar.
 */
function bypass(reason: ThreeDsBypassReason, detail: string): AuthenticateResult {
  console.warn(`[z2pay-3ds] bypass ${reason}: ${detail}`);
  return { status: 'bypassed', bypassReason: reason, bypassDetail: detail };
}

/**
 * Falha dura (D15): desafio exibido ao comprador e não concluído. O caller
 * aborta a venda. `proof` pode não existir — timeout no meio da prova nunca
 * produz comprovante.
 */
function failed(detail: string, proof?: ThreeDsProof): AuthenticateResult {
  console.warn(`[z2pay-3ds] failed: ${detail}`);
  return { status: 'failed', ...(proof ? { proof } : {}), failureDetail: detail };
}

function describeError(error: unknown): string {
  if (error instanceof Error) {
    // O TDSError da Stone guarda o corpo da resposta do servidor em
    // `errorResponse` ({ error: { code, message, details… }, status }) — é lá
    // que mora o campo exato recusado; `message` sozinha é genérica
    // ("Invalid request payload").
    const rich = error as Error & { errorResponse?: unknown; httpStatus?: unknown };
    const parts = [error.message];
    if (rich.httpStatus !== undefined) parts.push(`http=${String(rich.httpStatus)}`);
    if (rich.errorResponse !== undefined) {
      try {
        parts.push(JSON.stringify(rich.errorResponse));
      } catch {
        // corpo não serializável — segue só com message/status
      }
    }
    return parts.join(' | ').slice(0, 450);
  }
  try {
    return JSON.stringify(error).slice(0, 400);
  } catch {
    return String(error);
  }
}

export async function authenticate3ds(
  input: AuthenticateInput,
  fetchPreflight: PreflightFetcher,
): Promise<AuthenticateResult> {
  let preflight: ThreeDsPreflightResponse;
  try {
    preflight = await fetchPreflight();
  } catch (error) {
    return bypass('error', `preflight falhou: ${describeError(error)}`);
  }
  return runFromPreflight(preflight, input, fetchPreflight, true);
}

async function runFromPreflight(
  preflight: ThreeDsPreflightResponse,
  input: AuthenticateInput,
  fetchPreflight: PreflightFetcher,
  allowTokenRefresh: boolean,
): Promise<AuthenticateResult> {
  if (preflight.kind === 'none') {
    return { status: 'bypassed', bypassReason: preflight.bypassReason ?? 'not_attempted' };
  }

  if (preflight.kind === 'fake') {
    return runFakeChallenge(preflight.gatewayConfigId, input);
  }

  // A Stone valida o order client-side e exige billing_address completo no
  // cartão — sem ele o init rejeita na hora ("billing_address Invalid input").
  // Cascata igual à da cobrança: endereço do comprador quando o caller o
  // passou, senão o default do gateway (vem no pré-flight — é o MESMO
  // `defaultBillingAddress` que o GatewayPaymentService aplica server-side).
  // Sem nenhuma das fontes: bypass determinístico, sem nem carregar o script.
  const billing = input.card?.billingAddress ?? billingFromDefault(preflight.defaultBillingAddress);
  if (!billing) {
    return bypass(
      'unavailable',
      'sem billing_address (comprador não informou e gateway sem default)',
    );
  }

  const mintedAt = Date.now();
  try {
    await withTimeout(loadScriptOnce(preflight.scriptUrl), CHALLENGE_TIMEOUT_MS);
  } catch (error) {
    return bypass('unavailable', `script 3DS não carregou: ${describeError(error)}`);
  }

  let token = preflight.token;
  if (allowTokenRefresh && Date.now() - mintedAt > TOKEN_STALE_MS) {
    try {
      const fresh = await fetchPreflight();
      if (fresh.kind !== 'pagarme_js') {
        return runFromPreflight(fresh, input, fetchPreflight, false);
      }
      token = fresh.token;
    } catch {
      // segue com o token original — se expirou, o init falha e vira bypass
    }
  }

  return runPagarmeChallenge(preflight.gatewayConfigId, token, input, billing);
}

/**
 * Converte o default do gateway pro shape do desafio, com a MESMA normalização
 * da cobrança (PagarmeCustomerMapper.buildAddress): line1 "número, rua, bairro"
 * e complemento vazio vira "N/A". Sem os campos essenciais → undefined.
 */
function billingFromDefault(
  d: ThreeDsDefaultBillingAddress | undefined,
): AuthenticateBillingAddress | undefined {
  if (!d) return undefined;
  if (!d.zipCode || !d.street || !d.city || !d.state) return undefined;
  return {
    line1: [d.number, d.street, d.neighborhood].filter(Boolean).join(', '),
    line2: d.complement || 'N/A',
    zipCode: d.zipCode,
    city: d.city,
    state: d.state,
    country: d.country ?? 'BR',
  };
}

// ─── Pagar.me/Stone (3ds2.min.js) ───────────────────────────

interface TdsResultEntry {
  tds_server_trans_id?: string;
  trans_status?: string;
  challenge_canceled?: boolean;
}

interface TdsGlobal {
  init(
    config: {
      token: string;
      tds_method_container_element: HTMLElement;
      challenge_container_element: HTMLElement;
      use_default_challenge_iframe_style?: boolean;
    },
    order: Record<string, unknown>,
  ): Promise<TdsResultEntry[]>;
}

function getTds(): TdsGlobal | undefined {
  return (window as unknown as { TDS?: TdsGlobal }).TDS;
}

const scriptLoads = new Map<string, Promise<void>>();

function loadScriptOnce(url: string): Promise<void> {
  const existing = scriptLoads.get(url);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // remove do cache pra próxima tentativa poder recarregar
      scriptLoads.delete(url);
      script.remove();
      reject(new Error(`3DS script failed to load: ${url}`));
    };
    document.head.appendChild(script);
  });
  scriptLoads.set(url, promise);
  return promise;
}

async function runPagarmeChallenge(
  gatewayConfigId: string,
  token: string,
  input: AuthenticateInput,
  billing: AuthenticateBillingAddress,
): Promise<AuthenticateResult> {
  const tds = getTds();
  if (!tds) return bypass('unavailable', 'script carregou mas window.TDS não existe');

  // Containers exigidos pela Stone: o 3DS Method roda invisível; o iframe do
  // desafio vai dentro do container que o integrador passou.
  const methodContainer = document.createElement('div');
  methodContainer.style.display = 'none';
  document.body.appendChild(methodContainer);

  const challengeContainer = document.createElement('div');
  input.container.appendChild(challengeContainer);

  // Latch: tudo que a Stone insere aqui é UI do desafio (o 3DS Method roda no
  // methodContainer oculto). Uma vez exibido, fica exibido — mesmo que o
  // script remova o iframe depois — e o deadline passa pro prazo do comprador.
  let challengeShown = false;
  const deadline = createDeadline(CHALLENGE_TIMEOUT_MS);
  const observer = new MutationObserver(() => {
    if (challengeContainer.childElementCount === 0) return;
    challengeShown = true;
    deadline.extend(CHALLENGE_VISIBLE_TIMEOUT_MS);
    observer.disconnect();
  });
  observer.observe(challengeContainer, { childList: true });

  try {
    const results = await Promise.race([
      tds.init(
        {
          token,
          tds_method_container_element: methodContainer,
          challenge_container_element: challengeContainer,
          use_default_challenge_iframe_style: true,
        },
        buildTdsOrder(input, billing),
      ),
      deadline.promise,
    ]);
    return mapTdsResult(gatewayConfigId, results?.[0], challengeShown);
  } catch (error) {
    const detail = `TDS.init rejeitou: ${describeError(error)}`;
    // Desafio já na tela: timeout/erro aqui é "comprador não concluiu a prova"
    // → a venda NÃO segue. Bypass fica restrito a falha antes de exibir (D15).
    return challengeShown ? failed(detail) : bypass('unavailable', detail);
  } finally {
    observer.disconnect();
    deadline.clear();
    methodContainer.remove();
    challengeContainer.remove();
  }
}

function buildTdsOrder(
  input: AuthenticateInput,
  billing: AuthenticateBillingAddress,
): Record<string, unknown> {
  const digits = (value: string): string => value.replace(/\D/g, '');

  const payment: Record<string, unknown> = {
    payment_method: 'credit_card',
    amount: input.amount,
  };
  if (input.card) {
    payment.credit_card = {
      card: {
        number: digits(input.card.number),
        holder_name: input.card.holderName,
        exp_month: input.card.expMonth,
        exp_year: input.card.expYear < 100 ? 2000 + input.card.expYear : input.card.expYear,
        billing_address: {
          country: (billing.country ?? 'BR').toUpperCase(),
          state: billing.state,
          city: billing.city,
          zip_code: digits(billing.zipCode),
          line_1: billing.line1,
          // A chave line_2 é obrigatória mesmo sem complemento — vazio é aceito;
          // "N/A" mantém a paridade com a normalização da cobrança.
          line_2: billing.line2 ?? 'N/A',
        },
      },
    };
  }

  const order: Record<string, unknown> = {
    payments: [payment],
    // O /auth da Stone exige shipping_address quando a entrega não é
    // eletrônica — e sem o objeto shipping o script deles envia
    // electronic_delivery=false por default, derrubando TODO desafio com 422
    // ("shipping_address required_if"). A cobrança não envia shipping nenhum
    // ao Pagar.me e o checkout de hoje é entrega digital; declarar é o
    // verdadeiro. Com shipping presente o script exige recipient_name — nome
    // do comprador, com fallback no titular do cartão. Quando produto físico
    // existir (item.type physical), este default precisa acompanhar o
    // endereço de entrega real.
    shipping: {
      recipient_name: input.customer?.name ?? input.card?.holderName ?? 'N/A',
      electronic_delivery: true,
    },
  };
  if (input.customer) {
    const customer: Record<string, unknown> = {};
    if (input.customer.name) customer.name = input.customer.name;
    if (input.customer.email) customer.email = input.customer.email;
    if (input.customer.document) customer.document = digits(input.customer.document);
    if (Object.keys(customer).length > 0) order.customer = customer;
  }
  return order;
}

function mapTdsResult(
  gatewayConfigId: string,
  entry: TdsResultEntry | undefined,
  challengeShown: boolean,
): AuthenticateResult {
  if (!entry?.tds_server_trans_id) {
    const detail = entry
      ? `init resolveu sem tds_server_trans_id (trans_status=${entry.trans_status ?? 'ausente'})`
      : 'init resolveu sem nenhum resultado no array';
    return challengeShown ? failed(detail) : bypass('unavailable', detail);
  }

  const transStatus = entry.trans_status ?? 'N';
  const proof: ThreeDsProof = {
    gatewayConfigId,
    externalId: entry.tds_server_trans_id,
    transStatus,
  };

  if (entry.challenge_canceled) return { status: 'failed', proof };
  if (transStatus === 'Y') return { status: 'authenticated', proof };
  if (transStatus === 'A') return { status: 'attempted', proof };
  if (transStatus === 'N' || transStatus === 'R') return { status: 'failed', proof };
  // C/U/I e afins: sem veredito. Com desafio exibido = comprador não concluiu
  // → venda não segue (D15); sem desafio na tela (ARes U/I direto) → bypass.
  const detail = `trans_status=${transStatus} sem veredito (challenge_canceled=${entry.challenge_canceled ?? false})`;
  return challengeShown ? failed(detail, proof) : bypass('unavailable', detail);
}

// ─── Desafio simulado (sandbox / FakeProvider) ──────────────

async function runFakeChallenge(
  gatewayConfigId: string,
  input: AuthenticateInput,
): Promise<AuthenticateResult> {
  const last4 = (input.card?.number ?? '').replace(/\D/g, '').slice(-4);

  if (last4 === '4003') return { status: 'bypassed', bypassReason: 'unavailable' };

  const showsPanel = last4 === '4001' || last4 === '4002';
  if (showsPanel) await showFakeBankPanel(input.container, FAKE_CHALLENGE_MS);

  const approved = last4 !== '4002';
  const proof: ThreeDsProof = {
    gatewayConfigId,
    externalId: `fake_3ds_${Date.now()}`,
    transStatus: approved ? 'Y' : 'N',
  };
  return approved ? { status: 'authenticated', proof } : { status: 'failed', proof };
}

/** Painel "banco fake" com contagem regressiva — resolve sozinho por timeout. */
function showFakeBankPanel(container: HTMLElement, durationMs: number): Promise<void> {
  return new Promise((resolve) => {
    const panel = document.createElement('div');
    panel.setAttribute(
      'style',
      'border:1px solid #d0d0d0;border-radius:8px;background:#fff;color:#333;' +
        'font-family:system-ui,-apple-system,sans-serif;max-width:360px;margin:0 auto;' +
        'padding:24px;text-align:center;',
    );

    const title = document.createElement('div');
    title.setAttribute('style', 'font-size:16px;font-weight:600;margin-bottom:8px;');
    title.textContent = 'Confirmação do seu banco';

    const text = document.createElement('div');
    text.setAttribute('style', 'font-size:14px;color:#666;margin-bottom:16px;');
    text.textContent = 'Ambiente de teste: este desafio simulado se resolve sozinho.';

    const countdown = document.createElement('div');
    countdown.setAttribute('style', 'font-size:28px;font-weight:700;');

    panel.append(title, text, countdown);
    container.appendChild(panel);

    let remaining = Math.ceil(durationMs / 1000);
    countdown.textContent = String(remaining);
    const interval = window.setInterval(() => {
      remaining -= 1;
      countdown.textContent = String(Math.max(remaining, 0));
    }, 1000);

    window.setTimeout(() => {
      window.clearInterval(interval);
      panel.remove();
      resolve();
    }, durationMs);
  });
}

// ─── Util ───────────────────────────────────────────────────

interface Deadline {
  /** Rejeita com `Error('3DS timeout')` quando o prazo vigente estoura. */
  promise: Promise<never>;
  /** Reprograma o prazo a partir de agora (usado quando o desafio aparece). */
  extend(ms: number): void;
  clear(): void;
}

/** `withTimeout` não serve aqui: o prazo precisa ser estendido em voo. */
function createDeadline(ms: number): Deadline {
  let timer = 0;
  let fail: (error: Error) => void = () => {};
  const promise = new Promise<never>((_, reject) => {
    fail = reject;
  });
  const arm = (delayMs: number): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fail(new Error('3DS timeout')), delayMs);
  };
  arm(ms);
  return { promise, extend: arm, clear: () => window.clearTimeout(timer) };
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('3DS timeout')), ms);
    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });
}
